import { HttpClient, HttpContext, HttpErrorResponse, HttpParams } from '@angular/common/http'
import { Injectable, inject, signal } from '@angular/core'
import {
  CreditCardLoanResponse,
  CreditCardResponseDto,
  CreditCardWithdrawResponseDto,
  Customer,
  DashboardAccessResponseDto,
  DashboardDataResponseDto,
  LoanDocumentResponseDto,
  UnreadMessagesCountResponseDto,
  UserResponse,
} from '@visa/shared/interfaces'
import { firstValueFrom, Observable, throwError } from 'rxjs'
import { catchError, tap } from 'rxjs/operators'
import {
  AggregatedCustomerData,
  BankIdResponse,
  BankItem,
  BankLink,
  ConvertLoanToCardResponseDto,
  CustomerDetailsResponseDto,
  HousingCostVerificationResponseDto,
  HousingCostVerificationData,
  Invoice,
  LoanFeesResponse,
  NemidResponseDto,
  PaymentFreeMonthResult,
  PaymentFreeMonthStatus,
  PaymentSchedule,
  ServiceNotification,
  SignupDetails,
  Transaction,
  TransactionData,
  VerifyPhoneCodeResponseDto,
  RestartMonthioRulesCheckResponseDto,
  GetBankIdLinkResponseDto,
  UpdateCustomerDetailsResponseDto,
  TransactionsCheckResponseDto,
  GetActiveLoanResponseDto,
  GetBankidCheckUrlResponseDto,
  AuthUserResponseDto,
  HandleBankidVerificationResponseDto,
} from '../interfaces'
import { BankLoginMethods, ListOfValues, ListOfValuesResponseDto, RenewCustomerDetailsResponseDto } from '../types'
import { GetForwardInterest, LoadingBankIdLink, UpdatingCustomerDetails } from '@shared/services/loading.interceptor'
import { CustomerDetailsForm } from '@src/app/flow/interfaces/forms'

@Injectable({ providedIn: 'root' })
export class ApiService {
  private http = inject(HttpClient)

  readonly error$ = signal('')
  readonly errorSlot$ = signal('')
  readonly success$ = signal('')
  readonly successSlot$ = signal('')

  // istanbul ignore next
  fetchErrorFromResponse(errorResponse: HttpErrorResponse): string {
    if (errorResponse.status === 0) {
      return 'MessagesBag.networkError'
    }
    return errorResponse?.error?.error?.alias ?? errorResponse.message
  }

  handleError(errorResponse: HttpErrorResponse) {
    // istanbul ignore next
    const error = this.fetchErrorFromResponse(errorResponse)
    this.error$.set(error)
    this.success$.set('')
    return throwError(() => error)
  }

  handleErrorSlot(errorResponse: HttpErrorResponse) {
    // istanbul ignore next
    const error = this.fetchErrorFromResponse(errorResponse)
    this.errorSlot$.set(error)
    this.successSlot$.set('')
    return throwError(() => error)
  }

  handleSuccess(success: any) {
    this.error$.set('')
    this.success$.set(success)
  }

  handleSuccessSlot(success: any) {
    this.errorSlot$.set('')
    this.successSlot$.set(success)
  }

  /* Dashboard */
  applyToPaymentFreeMonth(): Observable<PaymentFreeMonthResult> {
    return this.http
      .post<PaymentFreeMonthResult>(`/api/v1/dashboard/users/payment-free-month`, {})
      .pipe(catchError(err => this.handleError(err)))
  }

  checkIfCanGetFreeMonth(): Observable<PaymentFreeMonthStatus> {
    return this.http
      .get<PaymentFreeMonthStatus>(`/api/v1/dashboard/users/payment-free-month`)
      .pipe(catchError(err => this.handleError(err)))
  }

  downloadInvoice(id: number, type: string) {
    return this.http.get(`/api/v1/dashboard/invoices/${id}`, {
      params: new HttpParams().set('type', type),
      responseType: 'blob',
    })
  }

  logout() {
    return firstValueFrom(this.http.post(`/api/v1/users/logout`, {}))
  }

  downloadAgreement() {
    return this.http.get(`/api/v1/dashboard/agreement/download`, { responseType: 'blob' })
  }

  getActiveLoan(): Promise<GetActiveLoanResponseDto> {
    return firstValueFrom(this.http.get<GetActiveLoanResponseDto>('/api/v1/dashboard/loans/active'))
  }

  getInvoices(invoicesNumber: number) {
    let params = {}
    if (invoicesNumber) {
      params = { invoicesNumber }
    }
    return this.http.get<Invoice[]>(`/api/v1/dashboard/invoices`, { params })
  }

  getForwardInterest(date: string) {
    return firstValueFrom(
      this.http
        .get<any>('/api/v1/dashboard/forward-interest', {
          params: { date },
          context: new HttpContext().set(GetForwardInterest, true),
        })
        .pipe(catchError(err => this.handleError(err))),
    )
  }

  getServiceNotification(): Observable<ServiceNotification | null> {
    return this.http
      .get<ServiceNotification | null>(`/api/public/v1/notifications/service`)
      .pipe(catchError(err => this.handleError(err)))
  }

  getPaymentFees(amount: number): Observable<LoanFeesResponse> {
    return this.http
      .get<LoanFeesResponse>(`/api/v1/dashboard/loan/fees`, { params: new HttpParams().set('amount', String(amount)) })
      .pipe(catchError(err => this.handleError(err)))
  }

  getTransactions() {
    return this.http.get<Transaction[]>(`/api/v1/dashboard/transactions`).pipe(catchError(err => this.handleError(err)))
  }

  getCustomerDetails(): Observable<CustomerDetailsResponseDto> {
    return this.http
      .get<CustomerDetailsResponseDto>(`/api/v1/dashboard/details`)
      .pipe(catchError(err => this.handleError(err)))
  }

  updateProfile(field: string, value: string) {
    return this.http.patch(`/api/v1/users`, { field, value })
  }

  withdraw(amount: number) {
    return this.http.post<any>(`/api/v1/dashboard/withdrawals`, { amount, fastLoan: false }).pipe(
      tap(() => this.handleSuccess('MessagesBag.withdrawSuccess')),
      catchError(err => this.handleError(err)),
    )
  }

  getPaymentSchedule(): Observable<PaymentSchedule> {
    return this.http
      .get<PaymentSchedule>(`/api/v1/withdrawals/payment-schedule`)
      .pipe(catchError(err => this.handleError(err)))
  }

  setupPaymentSchedule(accept: boolean): Observable<any> {
    return this.http
      .post<any>(`/api/v1/withdrawals/payment-schedule`, { accept })
      .pipe(catchError(err => this.handleError(err)))
  }

  cancelPayout() {
    return this.http.patch<any>(`/api/v1/dashboard/payout/cancel`, {}).pipe(
      tap(() => this.handleSuccessSlot('HomePage.payoutCancellationSuccess')),
      catchError(err => this.handleErrorSlot(err)),
    )
  }

  raiseCreditLimit() {
    return this.http.post<any>(`/api/v1/dashboard/credit-limit-raise`, {}).pipe(
      tap(() => this.handleSuccess('HomePage.creditLimitUpgradeSuccess')),
      catchError(err => this.handleError(err)),
    )
  }

  convertLoanToCard(): Observable<ConvertLoanToCardResponseDto> {
    return this.http
      .post<ConvertLoanToCardResponseDto>(`/api/v1/flow/conversion/step_1`, {})
      .pipe(catchError(err => this.handleError(err)))
  }

  startLoanToCardConversion(): Observable<ConvertLoanToCardResponseDto> {
    return this.http
      .patch<ConvertLoanToCardResponseDto>(`/api/v1/flow/conversion/step_2`, {})
      .pipe(catchError(err => this.handleError(err)))
  }

  confirmKycUpdate(): Observable<any> {
    return this.http
      .post<any>(`/api/v1/kyc/confirm_warning`, { confirm: true })
      .pipe(catchError(err => this.handleError(err)))
  }

  verifyBankIdCode(code: string, phone: string = ''): Promise<BankIdResponse> {
    return firstValueFrom(this.http.post<BankIdResponse>(`/api/public/v1/bankid/auth/verify`, { code, phone }))
  }

  getBanksList(): Observable<BankItem[]> {
    return this.http.get<BankItem[]>(`/api/v1/dashboard/banks`).pipe(catchError(err => this.handleError(err)))
  }

  getBankVerificationLink(bankId: string, callbackUrl: string): Observable<BankLink> {
    return this.http
      .get<BankLink>(`/api/v1/dashboard/banks/${bankId}/link`, {
        params: new HttpParams().set('callbackUrl', callbackUrl),
      })
      .pipe(catchError(err => this.handleError(err)))
  }

  getBankVerificationResults(requisitionId: string): Observable<any> {
    return this.http
      .get<any>(`/api/v1/dashboard/banks/verification`, { params: new HttpParams().set('requisition', requisitionId) })
      .pipe(catchError(err => this.handleError(err)))
  }

  getPaymentLink(amount: number, redirectUri: string): Observable<any> {
    return this.http
      .get<any>(`/api/v1/dashboard/users/payments/link`, {
        params: new HttpParams().append('amount', amount.toString()).append('redirectUri', redirectUri),
      })
      .pipe(catchError(err => this.handleError(err)))
  }

  /* Flow */
  completeApplication(affiliatesData: string = ''): Promise<any> {
    return firstValueFrom(this.http.patch('/api/v1/flow/complete', { affiliatesData }))
  }

  getLoanAndCustomerByGuid(guid: string, brokers: boolean = false): Promise<AggregatedCustomerData> {
    let params = {}
    if (brokers) params = new HttpParams().set('brokers', 'true')
    return firstValueFrom(this.http.get<AggregatedCustomerData>(`/api/public/v1/flow/loans/guid/${guid}`, { params }))
  }

  getBankidCheckUrl(callback: string): Promise<GetBankidCheckUrlResponseDto> {
    return firstValueFrom(
      this.http.get<GetBankidCheckUrlResponseDto>(`/api/v1/flow/nemid-check/${BankLoginMethods.mitid}`, {
        params: new HttpParams().set('callback', callback),
      }),
    )
  }

  saveBankidResults(payload: any): Promise<any> {
    return firstValueFrom(this.http.post<any>(`/api/v1/flow/nemid-check/response`, payload))
  }

  handleBankidVerificationResponse(queryParams): Promise<HandleBankidVerificationResponseDto> {
    return firstValueFrom(
      this.http.post<HandleBankidVerificationResponseDto>(
        `/api/public/v1/flow/user-identity-verification-response`,
        queryParams,
      ),
    )
  }

  startMonthioRulesCheck(): Promise<RestartMonthioRulesCheckResponseDto> {
    return firstValueFrom(
      this.http
        .post<RestartMonthioRulesCheckResponseDto>('/api/v1/flow/restart-monthio-rules-check', {})
        .pipe(catchError(err => this.handleError(err))),
    )
  }

  triggerMonthioRulesCheck(): Promise<any> {
    return firstValueFrom(this.http.get('/api/v1/flow/trigger-monthio-rules-check'))
  }

  updateCardPassword(password: string): Observable<{ user: Customer }> {
    return this.http.patch<any>('/api/v1/users/self/card', { password }).pipe(catchError(err => this.handleError(err)))
  }

  renewCustomerDetails(data): Observable<RenewCustomerDetailsResponseDto> {
    return this.http
      .post<RenewCustomerDetailsResponseDto>(`/api/v1/kyc/submit_form`, { data })
      .pipe(catchError(err => this.handleError(err)))
  }

  listOfValues(option: ListOfValues): Observable<ListOfValuesResponseDto> {
    return this.http
      .get<ListOfValuesResponseDto>(`/api/public/v1/tools/list-of-values/${option}`)
      .pipe(catchError(err => this.handleError(err)))
  }

  finishLoanPaymentTypeSelection(): Promise<any> {
    return firstValueFrom(this.http.patch('/api/v1/flow/loans/finish-payment-type-selection', {}))
  }

  sendVerificationCode(user: SignupDetails, token: string): Promise<any> {
    return firstValueFrom(
      this.http.post<any>('/api/public/v1/flow/send-verification-code', {
        // token,
        user: { email: user.email, phone: user.phone },
      }),
    )
  }

  userIdentityVerification(user: SignupDetails, method: BankLoginMethods): Observable<any> {
    return this.http
      .post<any>(`/api/public/v1/flow/user-identity-verification/${method}`, { user })
      .pipe(catchError(err => this.handleError(err)))
  }

  updateCustomerEarningsComment(comment: string): Promise<HousingCostVerificationResponseDto> {
    return firstValueFrom(this.http.post<any>('/api/v1/flow/users/proof-earnings-comment', { comment }))
  }

  selectPaymentType(type: 'normal' | 'fast'): Observable<any> {
    return this.http.post<any>(`/api/v1/withdrawals?type=${type}`, {}).pipe(catchError(err => this.handleError(err)))
  }

  getFastPaymentStatus(): Observable<any> {
    return this.http
      .get<any>(`/api/public/v1/flow/payments/fast-payment`)
      .pipe(catchError(err => this.handleError(err)))
  }

  authUser(linkId: string): Promise<AuthUserResponseDto> {
    return firstValueFrom(this.http.post<AuthUserResponseDto>('/api/public/v1/users/auth', { link_id: linkId }))
  }

  cancelApp(guid: string): Promise<any> {
    return firstValueFrom(this.http.post<any>('/api/public/v1/flow/app/cancel', { guid }))
  }

  housingCostVerification(data: HousingCostVerificationData): Promise<any> {
    return firstValueFrom(this.http.post<any>('/api/v1/flow/users/housing-cost-verification', { data }))
  }

  uploadFile(formData: FormData): Observable<any> {
    return this.http
      .post<any>(`/api/v1/flow/users/proof-earnings`, formData, { observe: 'events', reportProgress: true })
      .pipe(catchError(err => this.handleError(err)))
  }

  verifyPhoneCode(user: SignupDetails): Promise<VerifyPhoneCodeResponseDto> {
    return firstValueFrom(
      this.http.post<VerifyPhoneCodeResponseDto>('/api/public/v1/flow/phone-verification', { user }),
    )
  }

  unsubscribeCustomer(guid: string): Promise<any> {
    return firstValueFrom(
      this.http.patch<any>('/api/public/v1/flow/customers/unsubscribe', { guid }).pipe(
        tap(() => this.handleSuccess('UnsubscribeCustomer.successMessage')),
        catchError(err => this.handleError(err)),
      ),
    )
  }

  trackCustomerData(cid: string, session: string, affiliateData: string, data: any): Observable<any> {
    data.cid = cid
    data.session = session
    data.affiliate_data = affiliateData
    return this.http.post<any>(`/api/public/v1/track/data`, { data })
  }

  trackCustomerNavigation(session: string, event: object): Observable<any> {
    return this.http.post<any>(`/api/public/v1/track/navigation`, { data: { session, event } })
  }

  // New visa dash

  getDashboardData(): Observable<DashboardDataResponseDto> {
    return this.http
      .get<DashboardDataResponseDto>(`/api/v1/dashboard`)
      .pipe(catchError(err => this.handleErrorSlot(err)))
  }

  getDashboardAccess(): Observable<DashboardAccessResponseDto> {
    return this.http
      .get<DashboardAccessResponseDto>(`/api/v1/dashboard/check-access`)
      .pipe(catchError(err => this.handleErrorSlot(err)))
  }

  getLoanDocuments(): Observable<LoanDocumentResponseDto> {
    return this.http
      .get<LoanDocumentResponseDto>(`/api/v1/dashboard/loans/documents`)
      .pipe(catchError(err => this.handleErrorSlot(err)))
  }

  getLoanDocument(type: string): Observable<Blob> {
    return this.http
      .get(`/api/v1/dashboard/loans/documents/${type}`, { responseType: 'blob' })
      .pipe(catchError(err => this.handleErrorSlot(err)))
  }

  getCreditCardLoan(): Observable<CreditCardLoanResponse> {
    return this.http.get<CreditCardLoanResponse>(`/api/v1/dashboard/loans/credit-card`)
  }

  getCreditCardDetails(publicKeyBase64: string): Observable<CreditCardResponseDto> {
    return this.http
      .post<CreditCardResponseDto>('/api/v1/dashboard/cards/details/encrypted', {
        data: { public_key: publicKeyBase64 },
      })
      .pipe(catchError(err => this.handleError(err)))
  }

  refreshUser() {
    return this.http
      .get<{ isCreditCardEnabled: boolean; user: UserResponse }>(`/api/v1/users/self`)
      .pipe(catchError(err => this.handleError(err)))
  }

  updateProfile2(user: Partial<Customer>) {
    return this.http.patch<UserResponse>(`/api/v1/users/self`, { user }).pipe(
      tap(() => this.handleSuccess('MessagesBag.profileUpdated')),
      catchError(err => this.handleError(err)),
    )
  }

  cardWithdraw(redirectUri: string, cancellationUri: string): Observable<CreditCardWithdrawResponseDto> {
    return this.http
      .post<CreditCardWithdrawResponseDto>(`/api/v1/withdrawals/credit-card`, { cancellationUri, redirectUri })
      .pipe(catchError(err => this.handleErrorSlot(err)))
  }

  getTransactions2(offset, limit, from, to, query): Observable<{ total: number; transactions: TransactionData[] }> {
    let params = new HttpParams().set('offset', offset).set('limit', limit)

    if (from) {
      params = params.set('from', from)
    }
    if (to) {
      params = params.set('to', to)
    }
    if (query) {
      params = params.set('query', query)
    }

    return this.http
      .get<{ total: number; transactions: TransactionData[] }>(`/api/v1/transactions`, { params })
      .pipe(catchError(err => this.handleError(err)))
  }

  getTransactionsReport(from: string | null = null, to: string | null = null) {
    let params = new HttpParams()

    if (from) {
      params = params.set('from', from)
    }
    if (to) {
      params = params.set('to', to)
    }

    return this.http
      .get(`/api/v1/transactions/report`, { params, responseType: 'blob' })
      .pipe(catchError(err => this.handleError(err)))
  }

  getPreAuthTransactions(): Observable<{ total: number; amount: number; transactions: TransactionData[] }> {
    return this.http
      .get<{ total: number; amount: number; transactions: TransactionData[] }>(`/api/v1/transactions/pre-auth`)
      .pipe(catchError(err => this.handleError(err)))
  }

  getUnreadMessagesNumber(): Observable<UnreadMessagesCountResponseDto> {
    return this.http
      .get<UnreadMessagesCountResponseDto>('/api/v1/messages/unread')
      .pipe(catchError(err => this.handleError(err)))
  }

  // Refactored ==================================================

  getBankIdLink(email: string = '', phone: string = ''): Promise<GetBankIdLinkResponseDto> {
    let params = new HttpParams()
    if (email) {
      params = params.set('email', email)
    }
    if (phone) {
      params = params.set('phone', phone)
    }
    return firstValueFrom(
      this.http.get<GetBankIdLinkResponseDto>('/api/public/v1/bankid/auth/link', {
        params,
        context: new HttpContext().set(LoadingBankIdLink, true),
      }),
    )
  }

  updateCustomerDetails(data: CustomerDetailsForm): Promise<UpdateCustomerDetailsResponseDto> {
    return firstValueFrom(
      this.http.patch<UpdateCustomerDetailsResponseDto>(
        '/api/v1/flow/users/details',
        { data },
        {
          context: new HttpContext().set(UpdatingCustomerDetails, true),
        },
      ),
    )
  }

  getInternalChecksStatus(): Promise<NemidResponseDto> {
    return firstValueFrom(this.http.post<NemidResponseDto>('/api/v1/flow/internal-checks', {}))
  }

  getTransactionsCheckUrl(): Promise<TransactionsCheckResponseDto> {
    return firstValueFrom(this.http.get<TransactionsCheckResponseDto>('/api/v1/flow/bank-transactions/widget'))
  }

  getTransactionsCheckStatusUrl(userId: string): Promise<TransactionsCheckResponseDto> {
    return firstValueFrom(
      this.http.get<TransactionsCheckResponseDto>('/api/public/v1/flow/bank-transactions/redirect', {
        params: new HttpParams().set('userId', userId),
      }),
    )
  }
}
